"use client"

import { Images } from "@/constants/images"
import { t } from "@/languages/i18n"
import { Button, FormInstance, Input, Select } from "antd"
import { Grip, Trash2 } from "lucide-react"
import Image from "next/image"
import { useCallback, useState } from "react"
import { ReactSortable } from "react-sortablejs"

export interface Inclusion {
  icon: string
  description: string
  label: string
  id: number
}

const InclusionForm = ({ form }: { form: FormInstance }) => {
  const initialInclusions = form.getFieldValue("inclusions")

  const [inclusions, setInclusions] = useState<Inclusion[]>(initialInclusions)

  // Danh sách các icon có sẵn cho inclusion
  const inclusionIcons = [
    { value: "tourGuideIcon", label: "Tour Guide Icon", icon: Images.tourGuideIcon },
    { value: "accommodationIcon", label: "Accommodation Icon", icon: Images.accommodationIcon },
    { value: "bikeIcon", label: "Bike Icon", icon: Images.bikeIcon },
    { value: "mealIcon", label: "Meal Icon", icon: Images.mealIcon },
    { value: "busIcon", label: "Bus Icon", icon: Images.busIcon },
    { value: "fruitIcon", label: "Fruit Icon", icon: Images.fruitIcon },
  ]

  const handleChange = (value: Inclusion[]) => {
    setInclusions(value)
    const filteredValue = value.filter(item => item.label && item.description)
    form.setFieldsValue({ inclusions: filteredValue })
  }

  const handleAddInclusion = () => {
    const maxId = inclusions && inclusions?.length > 0 ? Math.max(...(inclusions?.map(item => item.id) || [])) : 0

    handleChange([...(inclusions || []), { icon: "", description: "", label: "", id: maxId + 1 }])
  }

  const handleRemoveInclusion = (index: number) => {
    handleChange(inclusions?.filter((_, i) => i !== index) || [])
  }

  const handleUpdateInclusion = (index: number, key: string, value: string) => {
    handleChange(inclusions?.map((item, i) => (i === index ? { ...item, [key]: value } : item)) || [])
  }

  const handleSetList = useCallback(
    (newList: Inclusion[]) => {
      const hasChanged = JSON.stringify(newList) !== JSON.stringify(inclusions || [])
      if (hasChanged) {
        handleChange(newList)
      }
    },
    [inclusions, setInclusions],
  )

  const columns = [
    {
      label: "STT",
      key: "id",
      width: "16px",
    },
    {
      label: "Icon",
      key: "icon",
      width: "100px",
    },
    {
      label: t("Label"),
      key: "label",
      width: "1fr",
    },
    {
      key: "description",
      label: t("Description"),
      width: "2fr",
    },
    {
      label: t("Action"),
      key: "action",
      width: "12%",
    },
  ]

  return (
    <div className="w-full border border-gray-300 rounded-md py-4 bg-white flex flex-col items-center">
      <div
        className="grid gap-2 w-full px-4 text-base font-medium border-b border-gray-300 pb-2 text-center"
        style={{ gridTemplateColumns: columns.map(column => column.width).join(" ") }}
      >
        {columns.map(column => (
          <p key={column.key}>{column.label}</p>
        ))}
      </div>

      <ReactSortable
        list={inclusions || []}
        setList={handleSetList}
        handle=".drag-handle"
        animation={150}
        className={`w-full flex flex-col gap-2`}
      >
        {inclusions?.map((inclusion, index) => (
          <div
            key={inclusion?.id}
            className={`grid gap-2 w-full items-center px-4 border-b border-gray-300 py-2 [&[draggable=true]]:bg-gray-200 `}
            style={{ gridTemplateColumns: columns.map(column => column.width).join(" ") }}
          >
            <p className="min-w-4">{index + 1}.</p>
            <div className="flex gap-2 items-center">
              <Select
                placeholder="Chọn icon"
                style={{ width: 120 }}
                value={inclusion?.icon}
                onChange={value => handleUpdateInclusion(index, "icon", value)}
                options={inclusionIcons.map(item => ({
                  value: item.value,
                  label: (
                    <div className="flex items-center gap-2">
                      <Image src={item.icon} alt={item.label} width={16} height={16} />
                    </div>
                  ),
                }))}
              />
            </div>

            <Input value={inclusion?.label} onChange={e => handleUpdateInclusion(index, "label", e.target.value)} />

            <Input
              value={inclusion?.description}
              onChange={e => handleUpdateInclusion(index, "description", e.target.value)}
            />

            <div className="flex gap-2 items-center justify-center">
              <Button danger onClick={() => handleRemoveInclusion(index)}>
                <Trash2 className="w-4 h-4" />
              </Button>
              <Grip className="drag-handle cursor-grab active:text-orange-primary active:scale-125 hover:text-orange-primary hover:scale-125 transition-all duration-300" />
            </div>
          </div>
        ))}
      </ReactSortable>

      <Button className="mt-3" type="primary" onClick={handleAddInclusion}>
        {t("Add Inclusion")}
      </Button>
    </div>
  )
}

export default InclusionForm
